import * as React from "react";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Filter, Operators, TextFilter } from "@progress/kendo-react-data-tools";
import { filterBy, orderBy } from "@progress/kendo-data-query";
import { Input } from "@progress/kendo-react-inputs";
import Notifications from "../../../Notifications/Messages";
import APIConnector from "../../../apiconnector";
import CommonVariables from "../../../variables";
import { useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";

export default function SystemUser(props) {
    const emptyGuid = "00000000-0000-0000-0000-000000000000";

    const emptySystemUser = {
        systemuserid: emptyGuid,
        fullname: "",
        firstname: "",
        lastname: "",
        phonenumber: "",
        internalemailaddress: ""
    };

    const initialFilter = {
        logic: "and",
        filters: [
            {
                field: "fullname",
                operator: "contains",
                value: ""
            }
        ]
    };

    const [data, setData] = React.useState([]);
    const [filter, setFilter] = React.useState(initialFilter);
    const [sort, setSort] = React.useState([]);
    const [openForm, setOpenForm] = React.useState(false);
    const [isCreateMode, setIsCreateMode] = React.useState(false);
    const [editItem, setEditItem] = React.useState(emptySystemUser);
    const [validationErrors, setValidationErrors] = React.useState({});
    const [gridHeight, setGridHeight] = React.useState(window.innerHeight - 317);

    useEffect(() => {
        getSystemUserList();

        const handleResize = () => {
            setGridHeight(window.innerHeight - 317);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const systemUserFields = [
        { 
            name: "fullname",
            label: "Full Name",
            filter: TextFilter,
            operators: Operators.text,
            width: 300
        },
        { 
            name: "firstname",
            label: "First Name",
            filter: TextFilter,
            operators: Operators.text,
            width: 200
        },
        { 
            name: "lastname",
            label: "Last Name",
            filter: TextFilter,
            operators: Operators.text,
            width: 200
        },
        { 
            name: "phonenumber",
            label: "Phone Number",
            filter: TextFilter,
            operators: Operators.text,
            width: 200
        },
        { 
            name: "internalemailaddress",
            label: "Email",
            filter: TextFilter,
            operators: Operators.text,
            width: 300
        }
    ];

        const systemUserGridFields = systemUserFields
        .filter((field) => field.name)
        .map(({ width, ...field }) => field);


    const getSystemUserInputModel = () => {
        return {
            systemuserid: editItem.systemuserid,
            fullname: `${editItem.firstname} ${editItem.lastname}`.trim(),
            firstname: editItem.firstname,
            lastname: editItem.lastname,
            phonenumber: editItem.phonenumber,
            internalemailaddress: editItem.internalemailaddress
        };
    };

    const validateSystemUser = () => {
        const errors = {
            firstname: editItem.firstname && editItem.firstname.toString().trim() ? "" : "First Name is required.",
            lastname: editItem.lastname && editItem.lastname.toString().trim() ? "" : "Last Name is required.",
            internalemailaddress: editItem.internalemailaddress && editItem.internalemailaddress.toString().trim() ? "" : "Email is required."
        };

        setValidationErrors(errors);

        const hasErrors = Object.values(errors).some((errorMessage) => errorMessage);

        if (hasErrors) {
            Notifications.ShowWarningMessage("Please fill all required fields.");
        }

        return !hasErrors;
    };

    const clearValidationError = (fieldName) => {
        setValidationErrors((previousErrors) => ({
            ...previousErrors,
            [fieldName]: ""
        }));
    };

    const renderValidationError = (fieldName) => {
        if (!validationErrors[fieldName]) {
            return null;
        }

        return (
            <div style={{ color: "#d51923", fontSize: "12px", fontWeight: "bold", marginTop: "4px" }}>
                {validationErrors[fieldName]}
            </div>
        );
    };

    const handleFieldChange = (fieldName, value) => {
        setEditItem((previousItem) => {
            const updatedItem = {
                ...previousItem,
                [fieldName]: value || ""
            };

            return {
                ...updatedItem,
                fullname: `${updatedItem.firstname} ${updatedItem.lastname}`.trim()
            };
        });

        clearValidationError(fieldName);
    };

    const openCreateForm = () => {
        setIsCreateMode(true);
        setEditItem(emptySystemUser);
        setValidationErrors({});
        setOpenForm(true);
    };

    const enterEdit = (item) => {
        setIsCreateMode(false);
        setEditItem({
            systemuserid: item.systemuserid,
            fullname: item.fullname,
            firstname: item.firstname,
            lastname: item.lastname,
            phonenumber: item.phonenumber,
            internalemailaddress: item.internalemailaddress
        });
        setValidationErrors({});
        setOpenForm(true);
    };

    const closeForm = () => {
        setOpenForm(false);
        setEditItem(emptySystemUser);
        setValidationErrors({});
    };

    const saveSystemUser = () => {
        if (!validateSystemUser()) {
            return;
        }

        const inputModel = getSystemUserInputModel();
        const saveUrl = isCreateMode ? CommonVariables.urls.CreateSystemUser : CommonVariables.urls.UpdateSystemUser;

        props.onLoaderRaise(true, isCreateMode ? "Creating system user..." : "Updating system user...", { marginTop: "145px" });

        APIConnector.Post(saveUrl, inputModel).then(function (response) {
            props.onLoaderRaise(false);

            if (response.isSuccess === true) {
                Notifications.ShowSuccessMessage(isCreateMode ? "System user created successfully." : "System user updated successfully.");
                closeForm();
                getSystemUserList();
            }
            else {
                Notifications.ShowErrorMessage("Something went wrong. Please try again.");
            }
        });
    };

    const MyEditCommandCell = (props) => (
        <EditCommandCell {...props} enterEdit={enterEdit} />
    );

    const EditCommandCell = (props) => {
        return (
            <td style={{ textAlign: "center" }}>
                <span title="Edit">
                    <EditIcon onClick={() => props.enterEdit(props.dataItem)} title="Edit" style={{ display: "inline-block", borderColor: "transparent", color: "#5454ec", cursor: "pointer" }} />
                </span>
            </td>
        );
    };

    const getSystemUserList = async () => {
        props.onLoaderRaise(true);

        APIConnector.Get(CommonVariables.urls.GetAllSystemUsers).then(function (response) {
            props.onLoaderRaise(false);
            if (response && response.length > 0 ) {
                setData(response);
            }
        });
    };
    const gridData = orderBy(filterBy(data, filter), sort);

    const grid = (
        <Grid
            className="rounded-2xl border-b-4"
            style={{ height: gridHeight > 300 ? gridHeight + "px" : "300px", width: "100%" }}
            data={gridData}
            filter={filter}
            onFilterChange={(event) => setFilter(event.filter)}
            sortable={true}
            sort={sort}
            onSortChange={(e) => setSort(e.sort)}
            pageable={true}
            take={1000}
            total={gridData.length}
            resizable={true}
            selectable={{
                enabled: true,
                drag: false,
                cell: false,
                mode: "single"
            }}
        >
            <GridNoRecords>
                No System Users found.
            </GridNoRecords>
            <Column field="firstname" width="280" title="First Name" />
            <Column field="lastname" width="280" title="Last Name" />
            <Column field="phonenumber" title="Phone Number" width="250" />
            <Column field="internalemailaddress" width="350" title="Email" />
            <Column cell={MyEditCommandCell} width="120" title="Edit" />
        </Grid>
    );

    return (
        <div style={{ width: "100%" }}>
            <div style={{ width: "calc(100vw - 48px)", maxWidth: "1280px", margin: "0 auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end"}}>
                    <div style={{ width: "660px" }}>
                    <Filter
                        value={filter}
                        onChange={(e) => setFilter(e.filter)}
                        fields={systemUserGridFields}
                    />
                    </div>
                    <div>
                    <button type="button" className="button-save" onClick={openCreateForm}>
                        Create System User
                    </button>
                    </div>
                </div>

                {grid}
            </div>

            {openForm && (
                <Dialog title={isCreateMode ? "Create System User" : "Edit System User"} onClose={closeForm} width={520}>
                    <div className="row">
                        <div className="col-12 mb-3">
                            <label className="fieldlabel">First Name <span style={{ color: "#d51923" }}>*</span></label>
                            <Input value={editItem.firstname} onChange={(event) => handleFieldChange("firstname", event.value)} />
                            {renderValidationError("firstname")}
                        </div>

                        <div className="col-12 mb-3">
                            <label className="fieldlabel">Last Name <span style={{ color: "#d51923" }}>*</span></label>
                            <Input value={editItem.lastname} onChange={(event) => handleFieldChange("lastname", event.value)} />
                            {renderValidationError("lastname")}
                        </div>

                        <div className="col-12 mb-3">
                            <label className="fieldlabel">Full Name</label>
                            <Input value={`${editItem.firstname} ${editItem.lastname}`.trim()} disabled={true} />
                        </div>

                        <div className="col-12 mb-3">
                            <label className="fieldlabel">Phone Number</label>
                            <Input value={editItem.phonenumber} onChange={(event) => handleFieldChange("phonenumber", event.value)} />
                        </div>

                        <div className="col-12 mb-3">
                            <label className="fieldlabel">Email <span style={{ color: "#d51923" }}>*</span></label>
                            <Input disabled={!isCreateMode} value={editItem.internalemailaddress} onChange={(event) => handleFieldChange("internalemailaddress", event.value)} />
                            {renderValidationError("internalemailaddress")}
                        </div>
                    </div>

                    <div className="k-form-buttons">
                        <button type="button" className="button-save" onClick={saveSystemUser}>
                            {isCreateMode ? "Create" : "Update"}
                        </button>

                        <button type="button" className="button-cancel" onClick={closeForm}>
                            Cancel
                        </button>
                    </div>
                </Dialog>
            )}
        </div>
    );
}